import { Component, Input, Output, EventEmitter, ViewEncapsulation, ViewChild } from '@angular/core';
import { switchMap, tap, map } from 'rxjs/operators';

import { ProjectService, VenueService } from '../../../providers';
import { CUSTOM_LOCATION_KEY, Venue } from '../../../models';

import { AutoCompleteComponent } from '../auto-complete/auto-complete.component';

@Component({
    selector: 'auto-complete-venues',
    templateUrl: './auto-complete-venues.component.html',
    styleUrls: ['./auto-complete-venues.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AutoCompleteVenuesComponent {

    @Output() selectionChanged = new EventEmitter<Venue[]>();
    @Output() createVenue = new EventEmitter<Venue>();

    // Set the maximum number of venues to be selected at a time
    @Input() maxMultiSelect: number = 1;

    @Input() set initSelectedList(venues: Array<Venue>) {
        // Init venue list with venues already selected (OBS this will happen every time an change is emitted)
        // Make of slice copy of array (otherwise functions like pop() will change the original array)
        this.selectedVenues = venues.slice(0);
    }

    @Input() set initSelectedKeys(venueKeys: Array<string>) {
        // Same as initSelectedList but with keys
        // Init venue list with venues already selected (OBS this will happen every time an change is emitted)
        // Make of slice copy of array (otherwise functions like pop() will change the original array)
        this.selectedVenueKeys = venueKeys.slice(0);
    }

    @ViewChild('VenueAutoComplete', { static: true }) autoCompleteComponent: AutoCompleteComponent;

    // List of user selected / venues
    public selectedVenues: Array<Venue> = [];
    public selectedVenueKeys: Array<string> = null;

    // Observable to be used in autocompletion list
    public venuesObservable;
    // Filter function to select motes
    public filterFunction;
    public itemExistsFunction;

    // Make document available in view
    public document = document;

    constructor(private venueService: VenueService, private projectService: ProjectService) {
        // Wrap filter function in ES6 container in order to preserve conrext
        this.filterFunction = (item, idx, searchString) => this.filterVenues(item, idx, searchString);
        this.itemExistsFunction = (item, idx, searchString) => this.venueExists(item, idx, searchString);
    }

    ngOnInit() {
        let watchVenues = this.projectService.watchActiveProject().pipe(switchMap((ev) => {
            // Special venue to be selected for custom 'place on map'  
            let customLocationVenue = new Venue({ title: 'Custom Position [map editor]', projectKey: ev.project?.key }, CUSTOM_LOCATION_KEY);
            // Concat special venue to top of list
            return this.venueService.watchProjectVenues(ev.project.key).pipe(map(venueList => [customLocationVenue].concat(venueList)));
        }));

        // Get observable of venues to be used in list (don't subscribe)
        this.venuesObservable = watchVenues.pipe(tap((venues) => {
            if(this.selectedVenueKeys == null) return;
            // Find selected venues (from venue keys)
            this.selectedVenues = this.selectedVenueKeys.map(key => venues.find(p => (key == p.key))).filter(p => p ? true : false);
            // Initially notify listners
            this.selectionChanged.emit(this.selectedVenues);
        }));
    }

    public itemSelected(item: Venue) {
        // Add new item to selection
        this.selectedVenues.push(item);
        // Pop old items if max selection count is enabled
        if(this.maxMultiSelect > 0 && this.selectedVenues.length > this.maxMultiSelect) {
            this.selectedVenues.shift();
        }
        this.selectionChanged.emit(this.selectedVenues);
    }

    public itemUnselected(rmItem: Venue) {
        this.selectedVenues = this.selectedVenues.filter((item: Venue) => {
            // Remove mote from list (return false if this is the one to be remove)
            return !(item.key == rmItem.key);
        });
        this.selectionChanged.emit(this.selectedVenues);
    }

    public queryDeleted() {
        if(this.selectedVenues.length <= 0) return;
        // Delete last entry in list of venue
        this.selectedVenues.pop();
        this.selectionChanged.emit(this.selectedVenues);
    }

    public openList() {
        this.autoCompleteComponent.toggleOpen();
    }

    /**
     * Search and filter shown venues in drop down list
     */
    public filterVenues(item: Venue, idx: number, searchString: string): boolean {
        if(!searchString || idx >= 200) return false;

        // Filter away venues that are already selected
        // if(this.selectedVenues.find((searchVenue) => searchVenue.key == item.key)) return false;

        // Return all if * is used
        if(searchString == "*") return true;

        // Search without case sensitivity
        let query = searchString.toLocaleLowerCase();

        // Search for string matches
        return item.key.startsWith(query) || item.title.toLowerCase().includes(query);
    }

    public createItem(itemName: string) {
        // Get active project
        let projectKey = (this.projectService.activeProject) ? this.projectService.activeProject.key : '';
        if(!projectKey) return console.warn("Could not create venue, no active project key");

        // Create a new venue from query title
        let venue = new Venue();
        venue.projectKey = projectKey;
        venue.title = itemName;

        // this.venueService.saveVenue(venue);
        // Emit new venue and let parent fill the rest of the data before saving it
        this.createVenue.emit(venue);
    }

    public venueExists(item: Venue, idx: number, searchString: string): boolean {
        return item.title == searchString;
    }
}
